import { useState } from "react";
import { BiArrowBack, BiCheckCircle, BiXCircle, BiWallet, BiPurchaseTag, BiCreditCard } from "react-icons/bi";
import { FaRegCheckCircle, FaRegTimesCircle } from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const ClientPaymentFeedback = () => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const statusParam = queryParams.get("status");
    const status = Number(statusParam);
    const mode = queryParams.get("mode");
    const trackingCode = queryParams.get("trackingCode");
    const transactionId = queryParams.get("transactionId");
    const { username } = useParams();

    const isSuccess = status === 1;
    const isWallet = mode === 'wallet';
    const isPurchase = mode === 'purchase';

    return (
        <>
            {loading ? (
                <div className="flex justify-center items-center h-screen bg-white dark:!bg-gray-950">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-600 dark:!border-teal-400 border-t-transparent"></div>
                </div>
            ) : (
                <div className="min-h-screen py-8 px-4 bg-gray-50 dark:!bg-gray-950 transition-colors duration-300">
                    <div className="max-w-2xl mx-auto relative">
                        {/* Card */}
                        <div className="relative bg-white dark:!bg-gray-900 rounded-3xl shadow-2xl dark:!shadow-black/50 overflow-hidden transition-all duration-300 hover:shadow-3xl">
                            {/* Decorative gradient bar */}
                            <div className={`h-2 w-full ${isSuccess ? 'bg-gradient-to-r from-green-400 to-teal-500' : 'bg-gradient-to-r from-red-400 to-rose-500'}`}></div>
                            
                            <div className="p-8 md:p-10">
                                {/* Status Icon */}
                                <div className="flex justify-center mb-6">
                                    <div className={`relative ${isSuccess ? 'animate-bounce' : 'animate-pulse'}`}>
                                        <div className={`w-28 h-28 rounded-full flex items-center justify-center shadow-lg ${
                                            isSuccess 
                                                ? 'bg-green-100 dark:!bg-green-950/50' 
                                                : 'bg-red-100 dark:!bg-red-950/50'
                                        }`}>
                                            {isSuccess ? (
                                                <FaRegCheckCircle className="w-16 h-16 text-green-500 dark:!text-green-400" />
                                            ) : (
                                                <FaRegTimesCircle className="w-16 h-16 text-red-500 dark:!text-red-400" />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Title */}
                                <h2 className={`text-3xl font-bold text-center mb-2 ${
                                    isSuccess 
                                        ? 'text-green-600 dark:!text-green-400' 
                                        : 'text-red-600 dark:!text-red-400'
                                }`}>
                                    {isSuccess ? 'پرداخت موفق ' : 'پرداخت ناموفق '}
                                </h2>
                                
                                <p className="text-center text-gray-500 dark:!text-gray-400 mb-8">
                                    {isSuccess 
                                        ? 'تراکنش شما با موفقیت انجام شد' 
                                        : 'متأسفانه تراکنش شما با مشکل مواجه شد'}
                                </p>

                                {/* Details Box */}
                                <div className="bg-gray-50 dark:!bg-gray-800/60 rounded-2xl p-6 mb-8 border border-gray-100 dark:!border-gray-800">
                                    {/* Mode */}
                                    <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:!border-gray-700 last:border-0">
                                        <span className="flex items-center gap-2 text-gray-600 dark:!text-gray-300 font-medium">
                                            {isWallet ? (
                                                <BiWallet className="w-5 h-5 text-teal-600 dark:!text-teal-400" />
                                            ) : isPurchase ? (
                                                <BiPurchaseTag className="w-5 h-5 text-purple-600 dark:!text-purple-400" />
                                            ) : (
                                                <BiCreditCard className="w-5 h-5 text-gray-600 dark:!text-gray-400" />
                                            )}
                                            شرح پرداخت
                                        </span>
                                        <span className="text-gray-800 dark:!text-gray-100 font-bold">
                                            {isWallet ? 'افزایش اعتبار' : isPurchase ? 'خرید طرح' : '---'}
                                        </span>
                                    </div>

                                    {/* Tracking Code */}
                                    {trackingCode && trackingCode !== "none" && trackingCode !== "------" && trackingCode !== "None" && (
                                        <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:!border-gray-700">
                                            <span className="flex items-center gap-2 text-gray-600 dark:!text-gray-300 font-medium">
                                                <BiCreditCard className="w-5 h-5 text-blue-600 dark:!text-blue-400" />
                                                شماره پیگیری
                                            </span>
                                            <span className="text-gray-800 dark:!text-gray-100 font-bold font-mono bg-white dark:!bg-gray-900 px-3 py-1 rounded-lg border border-gray-200 dark:!border-gray-700">
                                                {trackingCode}
                                            </span>
                                        </div>
                                    )}

                                    {/* Transaction ID */}
                                    <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:!border-gray-700 last:border-0">
                                        <span className="flex items-center gap-2 text-gray-600 dark:!text-gray-300 font-medium">
                                            <BiCreditCard className="w-5 h-5 text-purple-600 dark:!text-purple-400" />
                                            شماره تراکنش
                                        </span>
                                        <span className="text-gray-800 dark:!text-gray-100 font-bold font-mono bg-white dark:!bg-gray-900 px-3 py-1 rounded-lg border border-gray-200 dark:!border-gray-700">
                                            {transactionId || '---'}
                                        </span>
                                    </div>

                                    {/* Status Badge */}
                                    <div className="flex items-center justify-between py-3">
                                        <span className="flex items-center gap-2 text-gray-600 dark:!text-gray-300 font-medium">
                                            <BiCheckCircle className="w-5 h-5 text-gray-600 dark:!text-gray-400" />
                                            وضعیت
                                        </span>
                                        <span className={`px-4 py-1.5 rounded-full text-sm font-bold ${
                                            isSuccess 
                                                ? 'bg-green-100 text-green-700 dark:!bg-green-950/60 dark:!text-green-300' 
                                                : 'bg-red-100 text-red-700 dark:!bg-red-950/60 dark:!text-red-300'
                                        }`}>
                                            {isSuccess ? 'موفق' : 'ناموفق'}
                                        </span>
                                    </div>
                                </div>

                                {/* Back Button */}
                                <button
                                    onClick={() => navigate(`/bot/dashboard/${username}`)}
                                    className="w-full group flex items-center justify-center gap-3 bg-gradient-to-r from-teal-600 to-teal-700 dark:!from-teal-700 dark:!to-teal-800 text-white px-6 py-4 rounded-2xl font-bold text-lg shadow-lg dark:!shadow-black/40 hover:shadow-xl dark:!hover:shadow-black/60 transform hover:scale-[1.02] transition-all duration-300"
                                >
                                    <span>بازگشت به داشبورد</span>
                                    <BiArrowBack className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
                                </button>

                                {/* Help Text */}
                                <p className="text-center text-gray-400 dark:!text-gray-500 text-sm mt-4">
                                    در صورت نیاز به راهنمایی با پشتیبانی تماس بگیرید
                                </p>
                            </div>
                        </div>

                        {/* Decorative elements */}
                        <div className="absolute top-20 right-10 w-64 h-64 bg-gradient-to-r from-teal-200/20 to-emerald-200/20 dark:!from-teal-900/10 dark:!to-emerald-900/10 rounded-full blur-3xl -z-10"></div>
                        <div className="absolute bottom-20 left-10 w-64 h-64 bg-gradient-to-r from-purple-200/20 to-pink-200/20 dark:!from-purple-900/10 dark:!to-pink-900/10 rounded-full blur-3xl -z-10"></div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ClientPaymentFeedback;